import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '../assets/icons';
import Button from './common/ui/Button';
import './HorizontalScrollList.css';

const HorizontalScrollList = ({ title, accentTitle, infoText, children, actionLabel, onAction }) => {
  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const directionRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [infoOpen, setInfoOpen] = useState(false);

  const updateScrollState = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const newCanScrollLeft = scrollLeft > 0;
      const newCanScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
      // Only update state when values actually change to avoid unnecessary re-renders
      setCanScrollLeft(prev => prev !== newCanScrollLeft ? newCanScrollLeft : prev);
      setCanScrollRight(prev => prev !== newCanScrollRight ? newCanScrollRight : prev);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      stopAutoScroll();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [updateScrollState]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 600;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const autoScroll = () => {
    if (!scrollRef.current || !directionRef.current) return;

    const el = scrollRef.current;
    const scrollSpeed = 12; // pixels per frame at 60fps

    if (directionRef.current === 'left') {
      el.scrollLeft -= scrollSpeed;
    } else {
      el.scrollLeft += scrollSpeed;
    }

    // Check if we've hit the boundary
    const { scrollLeft, scrollWidth, clientWidth } = el;
    if (directionRef.current === 'left' && scrollLeft <= 0) {
      stopAutoScroll();
      return;
    }
    if (directionRef.current === 'right' && scrollLeft >= scrollWidth - clientWidth) {
      stopAutoScroll();
      return;
    }

    rafRef.current = requestAnimationFrame(autoScroll);
  };

  const startAutoScroll = (direction) => {
    if (rafRef.current) return;
    directionRef.current = direction;
    rafRef.current = requestAnimationFrame(autoScroll);
  };

  const stopAutoScroll = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      directionRef.current = null;
    }
  };

  return (
    <section className="horizontal-scroll-section">
      <header className="horizontal-scroll__header">
        <div className="horizontal-scroll__heading">
          <h2 className="horizontal-scroll__title">
            {title}{accentTitle && <span className="horizontal-scroll__accent">{accentTitle}</span>}
          </h2>
          {infoText && (
            <button
              type="button"
              className="horizontal-scroll__info-btn"
              onClick={() => setInfoOpen((v) => !v)}
              aria-label={`About ${title}`}
              aria-expanded={infoOpen}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
          )}
          {infoText && infoOpen && (
            <div className="horizontal-scroll__info-popover" role="tooltip">
              {infoText}
            </div>
          )}
        </div>
        <div className="horizontal-scroll__controls">
          {actionLabel && onAction && (
            <button
              type="button"
              className="horizontal-scroll__view-all"
              onClick={onAction}
            >
              {actionLabel} <span className="horizontal-scroll__arrow">&rarr;</span>
            </button>
          )}
        </div>
      </header>

      <div className="horizontal-scroll__viewport-wrapper">
        {/* Left Edge - Hover Zone with Arrow */}
        {canScrollLeft && (
          <div className="scroll-edge scroll-edge--left">
            <button
              className="scroll-edge__arrow"
              onClick={() => scroll('left')}
              onMouseEnter={() => startAutoScroll('left')}
              onMouseLeave={stopAutoScroll}
              aria-label="Scroll left"
            >
              <ChevronLeftIcon className="scroll-edge__icon" />
            </button>
          </div>
        )}

        {/* Viewport */}
        <div className="horizontal-scroll__viewport" ref={scrollRef}>
          <div className="horizontal-scroll__track">
            {children}
          </div>
        </div>

        {/* Right Edge - Hover Zone with Arrow */}
        {canScrollRight && (
          <div className="scroll-edge scroll-edge--right">
            <button
              className="scroll-edge__arrow"
              onClick={() => scroll('right')}
              onMouseEnter={() => startAutoScroll('right')}
              onMouseLeave={stopAutoScroll}
              aria-label="Scroll right"
            >
              <ChevronRightIcon className="scroll-edge__icon" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default HorizontalScrollList;
